import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Space, 
  Row, 
  Col, 
  Upload, 
  message,
  Modal,
  Typography 
} from 'antd';
import { 
  DownloadOutlined, 
  UploadOutlined, 
  SearchOutlined 
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { academyTeacherApi } from '@/services/api';

const { Title } = Typography;
const DEFAULT_PAGE_SIZE = 10;

interface TeacherData {
  key: string;
  no: number;
  teacherId: string;
  loginId: string;
  teacherName: string;
  birthDate: string;
  status: 'normal' | 'withdrawn';
  registrationDate: string;
}

const TeacherManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialKeyword = searchParams.get('q') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialPageSize = Number(searchParams.get('size')) || DEFAULT_PAGE_SIZE;
  const [searchText, setSearchText] = useState(initialKeyword);
  const [rows, setRows] = useState<TeacherData[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [appliedKeyword, setAppliedKeyword] = useState(initialKeyword);

  const academyCode = searchParams.get('academyCode') || '';

  const syncSearchParams = useCallback((nextPage: number, nextSize: number, keyword: string) => {
    const params = new URLSearchParams();
    if (keyword) params.set('q', keyword);
    if (academyCode) params.set('academyCode', academyCode);
    if (nextPage > 1) params.set('page', String(nextPage));
    if (nextSize !== DEFAULT_PAGE_SIZE) params.set('size', String(nextSize));
    setSearchParams(params);
  }, [academyCode, setSearchParams]);

  const fetchList = useCallback(async (p = page, s = pageSize, keyword = appliedKeyword) => {
    try {
      const res: any = await academyTeacherApi.list({ academyCode: academyCode || undefined, q: keyword, page: p - 1, size: s });
      const pageData = res?.data || res;
      const content = pageData?.content || [];
      const mapped: TeacherData[] = content.map((it: any, idx: number) => ({
        key: String((pageData?.totalElements ?? content.length) - ((p - 1) * s + idx)),
        no: (pageData?.totalElements ?? content.length) - ((p - 1) * s + idx),
        teacherId: String(it.id),
        loginId: it.loginId || it.login_id || '-',
        teacherName: it.name,
        birthDate: it.birthDate || '-',
        status: it.status === '정상' ? 'normal' : 'withdrawn',
        registrationDate: it.registrationDate || '-',
      }));
      setRows(mapped);
      setTotal(pageData?.totalElements ?? mapped.length);
      setPage(pageData?.number != null ? (pageData.number + 1) : p);
      setPageSize(pageData?.size ?? s);
    } catch (e) {
      console.error(e);
      setRows([]);
      setTotal(0);
    }
  }, [academyCode, page, pageSize, appliedKeyword]);

  useEffect(() => {
    syncSearchParams(page, pageSize, appliedKeyword);
    fetchList(page, pageSize, appliedKeyword);
  }, [academyCode, page, pageSize, appliedKeyword, fetchList, syncSearchParams]);

  const handleSearch = () => {
    const keyword = searchText.trim();
    setPage(1);
    setAppliedKeyword(keyword);
  };

  const handleReset = () => {
    setSearchText('');
    setAppliedKeyword('');
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await academyTeacherApi.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'teacher_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error('템플릿 다운로드에 실패했습니다.');
    }
  };

  const handleUploadExcel = async (file: File) => {
    try {
      const payload: { academyCode?: string; file: File } = { file };
      if (academyCode) payload.academyCode = academyCode;
      const res: any = await academyTeacherApi.uploadExcel(payload);
      const response = res ?? {};
      const data = response?.data ?? response ?? {};
      const result = data?.data ?? data ?? {};
      const successCount = typeof result?.success === 'number' ? result.success : 0;
      const totalCount = typeof result?.total === 'number'
        ? result.total
        : Array.isArray(result?.results) ? result.results.length : 0;
      const failedRows = Array.isArray(result?.results)
        ? result.results.filter((row: any) => row?.status === 'failed')
        : [];
      const failCount = Math.max(totalCount - successCount, failedRows.length);
      if (successCount > 0) {
        message.success(`${successCount}/${totalCount || successCount}명 등록 완료`);
      } else {
        message.error(response?.message || '업로드에 실패했습니다.');
      }
      if (failCount > 0) {
        Modal.warning({
          title: '등록 실패 상세',
          width: 720,
          content: (
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {failedRows.length === 0 ? (
                <div>실패 내역을 확인할 수 없습니다.</div>
              ) : (
                <ul style={{ paddingLeft: 16 }}>
                  {failedRows.slice(0, 50).map((row: any, idx: number) => (
                    <li key={`${row.academyCode || '-'}-${row.loginId || idx}`}>
                      [{row.academyCode || '-'}] {row.loginId || '-'} - {row.message || '실패'}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        });
      }
      await fetchList(1, pageSize);
    } catch (e: any) {
      const errorMessage = e?.response?.data?.message || '형식이 올바르지 않습니다.';
      message.error(errorMessage);
    }
    return false; // antd Upload 자동 업로드 방지
  };

  const teacherColumns: ColumnsType<TeacherData> = [
    { 
      title: '순번', 
      dataIndex: 'no', 
      key: 'no', 
      width: 80, 
      align: 'center' 
    },
    {
      title: '강사ID',
      dataIndex: 'loginId',
      key: 'loginId',
      width: 140,
    },
    { 
      title: '강사명', 
      dataIndex: 'teacherName', 
      key: 'teacherName', 
      width: 120 
    },
    { 
      title: '생년월일', 
      dataIndex: 'birthDate', 
      key: 'birthDate', 
      width: 120 
    },
    { 
      title: '상태', 
      dataIndex: 'status', 
      key: 'status', 
      width: 120,
      render: (status: string) => {
        const statusConfig = {
          normal: { text: '정상', color: 'green' },
          withdrawn: { text: '퇴원', color: 'red' }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
          <span style={{ 
            color: config.color, 
            fontWeight: 'bold' 
          }}>
            {config.text}
          </span>
        );
      }
    },
    { 
      title: '등록일', 
      dataIndex: 'registrationDate', 
      key: 'registrationDate', 
      width: 120 
    }
  ];

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        <Row gutter={16} justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Title level={2}>
            강사관리
          </Title>

          {/* 버튼 영역 */}
          <Row gutter={16}>
            <Col>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />}
                onClick={handleDownloadTemplate}
                size="large"
              >
                강사계정생성 양식 다운로드
              </Button>
            </Col>
            <Col>
              <Upload
                beforeUpload={handleUploadExcel}
                accept=".xlsx,.xls"
                showUploadList={false}
              >
                <Button 
                  icon={<UploadOutlined />}
                  size="large"
                >
                  강사 계정 생성 엑셀 업로드
                </Button>
              </Upload>
            </Col>
          </Row>
        </Row>

        {/* 검색 영역 */}
        <Row gutter={16} align="middle" style={{ marginBottom: '16px' }}>
          <Col span={6}>
            <Input
              placeholder="강사명으로 검색"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col>
            <Space>
              <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
                검색
              </Button>
              <Button onClick={handleReset}>
                초기화
              </Button>
            </Space>
          </Col>
        </Row>

        {/* 테이블 */}
        <Table
          columns={teacherColumns}
          dataSource={rows}
          pagination={{
            total: total,
            current: page,
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t, range) => {
              if (!t) return `0-0 / 총 0개`;
              const start = Math.max(t - range[1] + 1, 1);
              const end = Math.max(t - range[0] + 1, 1);
              return `${start}-${end} / 총 ${t}개`;
            },
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ x: 600 }}
          onRow={(record) => ({
            onClick: () => navigate(
              `/admin/teacher/${record.teacherId}`,
              { state: { from: `${location.pathname}${location.search}` } },
            ),
            style: { cursor: 'pointer' }
          })}
        />
      </div>
    </AdminLayout>
  );
};

export default TeacherManagement; 